import {
  ZulipAttachmentResponse,
  ZulipConfig,
  ZulipMessage,
  ZulipMessageEvent,
  ZulipMessagesResponse,
} from './types';

export class ZulipApi {
  private readonly realm: string;
  private readonly apiKey: string;
  private readonly username: string;
  private readonly apiVersion: string = 'v1';

  constructor(config: ZulipConfig) {
    this.realm = config.realm.replace(/\/$/, '');
    this.apiKey = config.apiKey;
    this.username = config.username;
  }

  private get baseUrl(): string {
    return `${this.realm}/api/${this.apiVersion}`;
  }

  private get authHeader(): string {
    return `Basic ${btoa(`${this.username}:${this.apiKey}`)}`;
  }

  private async request<T>(
    endpoint: string,
    method = 'GET',
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any,
  ): Promise<T> {
    const url = `${this.baseUrl}/${endpoint}`;
    const headers = {
      'Authorization': this.authHeader,
      'Accept': 'application/json',
    };

    const options: RequestInit = {
      method,
      headers,
      mode: 'cors',
      body: data instanceof FormData ? data : JSON.stringify(data),
    };

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.msg || `Zulip API error: ${response.statusText}`,
      );
    }

    return await response.json();
  }

  async sendMessage(message: ZulipMessage): Promise<ZulipMessagesResponse> {
    let content = message.content;

    // If there are attachments, upload them first and add their links to the message
    const files = message.attachments;
    const fileArray = (Array.isArray(files) ? files : [files]).filter(
      (file): file is File => file !== undefined,
    );

    if (fileArray.length > 0) {
      const uploadPromises = fileArray.map(async (file) => {
        return await this.uploadAttachment(file);
      });
      const uploadResults = await Promise.all(uploadPromises);

      // Add attachment links to the message content
      const attachmentLinks = uploadResults.map((result) => {
        const fileName = result.uri.split('/').pop() as string;
        return `\n[${fileName}](${this.realm}${result.uri})`;
      });

      content += '\n' + attachmentLinks.join('\n');
    }

    const endpoint = 'messages';
    const data = new FormData();
    data.append('type', message.type);
    data.append(
      'to',
      Array.isArray(message.to) ? JSON.stringify(message.to) : message.to,
    );
    data.append('content', content);
    if (message.subject) {
      data.append('subject', message.subject);
    }
    data.append('topic', message.topic as string);
    return await this.request<ZulipMessagesResponse>(endpoint, 'POST', data);
  }

  private async uploadAttachment(file: File): Promise<ZulipAttachmentResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return await this.request<ZulipAttachmentResponse>(
      'user_uploads',
      'POST',
      formData,
    );
  }

  /**
   * Get a temporary URL for access to the file that doesn't require authentication.
   */
  async getPublicURL(filename: string): Promise<ZulipAttachmentResponse> {
    const realmIdStr = '2';
    const filepath = filename.split('/').pop() as string;

    return await this.request<ZulipAttachmentResponse>(
      `user_uploads/${realmIdStr}/${filepath}`,
      'GET',
    );
  }

  async getMessages(params: {
    anchor: 'newest' | 'oldest' | number;
    numBefore: number;
    numAfter: number;
    narrow?: object[];
    apply_markdown?: boolean;
  }): Promise<{ messages: ZulipMessageEvent[] }> {
    const searchParams = new URLSearchParams({
      anchor: params.anchor.toString(),
      num_before: params.numBefore.toString(),
      num_after: params.numAfter.toString(),
      narrow: params.narrow ? JSON.stringify(params.narrow) : '[]',
      apply_markdown: params.apply_markdown ? 'true' : 'false',
    });

    const response = this.request<ZulipMessagesResponse>(
      `messages?${searchParams.toString()}`,
    );
    return await response.then((res) => ({ messages: res.messages }));
  }
}
